import { useState } from "react";
import { Globe, Check } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const LANGUAGES = [
    { code: "en", label: "English" },
    { code: "hi", label: "हिन्दी" },
    { code: "mr", label: "मराठी" },
    { code: "gu", label: "ગુજરાતી" },
    { code: "ta", label: "தமிழ்" },
    { code: "te", label: "తెలుగు" },
    { code: "bn", label: "বাংলা" },
    { code: "kn", label: "ಕನ್ನಡ" },
    { code: "ml", label: "മലയാളം" },
    { code: "pa", label: "ਪੰਜਾਬੀ" },
    { code: "ur", label: "اردو" },
    { code: "es", label: "Español" },
    { code: "fr", label: "Français" },
    { code: "de", label: "Deutsch" },
    { code: "ar", label: "العربية" },
    { code: "pt", label: "Português" },
    { code: "ru", label: "Русский" },
    { code: "zh-CN", label: "中文" },
    { code: "ja", label: "日本語" },
];

function currentLang() {
    const m = document.cookie.match(/googtrans=\/en\/([a-zA-Z-]+)/);
    return m ? m[1] : "en";
}

function applyLanguage(code) {
    const trySet = (attempts) => {
        const combo = document.querySelector(".goog-te-combo");
        if (combo) {
            combo.value = code;
            combo.dispatchEvent(new Event("change"));
            return;
        }
        if (attempts > 0) setTimeout(() => trySet(attempts - 1), 400);
        else {
            // fallback: set cookie and reload
            document.cookie = `googtrans=/en/${code};path=/`;
            document.cookie = `googtrans=/en/${code};path=/;domain=${window.location.hostname}`;
            window.location.reload();
        }
    };
    trySet(12);
}

export const LanguageSwitcher = ({ light = false }) => {
    const [lang, setLang] = useState(currentLang());

    const pick = (code) => {
        setLang(code);
        applyLanguage(code);
    };

    const active = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="language-switcher-button"
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        light
                            ? "border-blue-200 bg-white/70 text-slate-600 hover:border-blue-400 hover:text-blue-700"
                            : "border-white/10 bg-white/5 text-slate-300 hover:border-cyan-500/40 hover:text-cyan-300"
                    }`}
                >
                    <Globe className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{active.label}</span>
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="max-h-80 overflow-y-auto border-white/10 bg-[#0D1424] text-slate-200"
                data-testid="language-switcher-menu"
            >
                {LANGUAGES.map((l) => (
                    <DropdownMenuItem
                        key={l.code}
                        data-testid={`language-option-${l.code}`}
                        onSelect={() => pick(l.code)}
                        className="flex items-center justify-between gap-3 cursor-pointer focus:bg-cyan-500/10 focus:text-cyan-300"
                    >
                        {l.label}
                        {lang === l.code && <Check className="h-3.5 w-3.5 text-cyan-400" />}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
